import * as THREE from "three";
import { brushedMaps, grainMaps, mapsFromTexture, type DetailMaps } from "./detail";
import type { ArtStyle, MaterialParams, Surface } from "./types";

// Realistic, first slice (#75): believable game-realistic, closer to THPS 1+2
// Remake than to film. Filmic (ACES) tone mapping, a sun and an environment
// lit from the map's own sky, softer and sharper shadows, a little more haze,
// no outlines or rim light, and surfaces with procedural normal and roughness
// maps: pored and cracked concrete, grained plywood, brushed steel, chipped
// paint. Same meshes as the cartoon style; the rider gets its own build
// (`rider`, below): the same bones in more human proportions.

/** How each surface differs from the call site's (cartoon-tuned) choices. */
interface SurfaceLook {
  roughness: number;
  metalness: number;
  /** Where its normal / roughness detail comes from: its own color map, brushed steel, a fine grain, or none. */
  detail: "own" | "brushed" | "grain" | null;
  /** How strongly the normal map bends the light. */
  bump: number;
  /** Take some of the toy saturation out of the color. */
  desaturate: number;
  /** Scale the color's lightness: worn steel is darker than the toy's bright grey. */
  darken?: number;
  /** Physical extras: anisotropic highlights for brushed metal, sheen for fabric. */
  anisotropy?: number;
  sheen?: number;
  /** Tints the sheen away from the surface color (skin's warm, blood-red edge). */
  sheenTint?: THREE.ColorRepresentation;
  clearcoat?: number;
}

const SURFACES: Record<Surface, SurfaceLook> = {
  plastic: { roughness: 0.5, metalness: 0, detail: "grain", bump: 0.15, desaturate: 0.1 },
  paint: { roughness: 0.6, metalness: 0, detail: "grain", bump: 0.25, desaturate: 0.15 },
  concrete: { roughness: 0.95, metalness: 0, detail: "own", bump: 1, desaturate: 0.2 },
  plywood: { roughness: 0.72, metalness: 0, detail: "own", bump: 0.8, desaturate: 0.15 },
  // #158: used galvanized steel, not a mirror. At roughness 0.32 with a strong
  // brushed streak the rails, posts and coping caught the sky and bloomed
  // white; now rough and dull, and darker, which on a metal also means it
  // reflects less (its color is its reflectance).
  // #188: the shine came back as white streaks on the coping and rails, the
  // sun's own highlight: roughness 0.6 (0.45 in the brushed map's smoothest
  // streaks) with anisotropy kept it several times past bloom's threshold,
  // and the map moods' S-curve (#179) took the rest to white. Rougher,
  // without anisotropy and a little darker, the sun's peak on the brightest
  // steel stays under the threshold on every map (`styles.test.ts`).
  metal: { roughness: 0.85, metalness: 1, detail: "brushed", bump: 0.5, desaturate: 0, darken: 0.65 },
  paintedMetal: { roughness: 0.6, metalness: 0.1, detail: "own", bump: 0.6, desaturate: 0.1, clearcoat: 0.1 },
  grass: { roughness: 1, metalness: 0, detail: "grain", bump: 1, desaturate: 0.25 },
  glass: { roughness: 0.08, metalness: 0.1, detail: null, bump: 0, desaturate: 0.1 },
  // A warm red sheen at grazing angles stands in for light scattering under the skin.
  skin: { roughness: 0.55, metalness: 0, detail: "grain", bump: 0.08, desaturate: 0, sheen: 0.35, sheenTint: 0xff5a3c },
  cloth: { roughness: 0.95, metalness: 0, detail: "own", bump: 0.5, desaturate: 0.1, sheen: 0.6 },
  denim: { roughness: 0.9, metalness: 0, detail: "own", bump: 0.7, desaturate: 0.1, sheen: 0.4 },
  leather: { roughness: 0.45, metalness: 0, detail: "grain", bump: 0.3, desaturate: 0.05, clearcoat: 0.2 },
  hair: { roughness: 0.6, metalness: 0, detail: "grain", bump: 0.4, desaturate: 0, sheen: 0.5 },
};

function detailFor(look: SurfaceLook, map: THREE.Texture | null | undefined): DetailMaps | null {
  switch (look.detail) {
    case "own":
      return map ? mapsFromTexture(map) : grainMaps(3);
    case "brushed":
      return brushedMaps(2);
    case "grain":
      return grainMaps(3);
    default:
      return null;
  }
}

export const realistic: ArtStyle = {
  id: "realistic",
  name: "Realistic",
  pitch: "Game-realistic: filmic light off the sky, soft shadows, pored concrete, grained wood, brushed steel. No outlines.",
  cost: "medium",

  material(color, params, surface) {
    const look = SURFACES[surface];
    const c = new THREE.Color(color);
    if (look.desaturate > 0 || look.darken !== undefined) {
      const hsl = { h: 0, s: 0, l: 0 };
      c.getHSL(hsl);
      c.setHSL(hsl.h, hsl.s * (1 - look.desaturate), hsl.l * (look.darken ?? 1));
    }
    const maps = detailFor(look, params.map);
    const p: MaterialParams = {
      ...params,
      color: c,
      roughness: look.roughness,
      metalness: look.metalness,
      ...(maps ? { normalMap: maps.normal, normalScale: new THREE.Vector2(look.bump, look.bump), roughnessMap: maps.roughness } : {}),
    };
    if (look.anisotropy || look.sheen || look.clearcoat) {
      return new THREE.MeshPhysicalMaterial({
        ...p,
        anisotropy: look.anisotropy ?? 0,
        sheen: look.sheen ?? 0,
        sheenColor: look.sheenTint === undefined ? c : c.clone().lerp(new THREE.Color(look.sheenTint), 0.6),
        sheenRoughness: 0.8,
        clearcoat: look.clearcoat ?? 0,
        clearcoatRoughness: 0.4,
      });
    }
    return new THREE.MeshStandardMaterial(p);
  },

  outlines: { scale: 0, ink: 0x000000, edges: 0 },

  lighting: {
    sunColor: 0xfff0dc,
    sun: 1.6,
    // The sky environment carries most of the ambient bounce.
    hemi: 0.25,
    environment: "sky",
    envIntensity: { outdoor: 0.45, indoor: 0.4 },
  },

  sky: { clouds: false },
  fog: { near: 0.6, far: 0.85 },

  toneMapping: THREE.ACESFilmicToneMapping,
  exposure: 0.8,

  // Soft contact shadows, a gentle glow on sunlit highlights, and a slightly
  // warm, contrasty grade with a faint vignette.
  post: {
    ao: { radius: 0.6, intensity: 0.85 },
    bloom: { strength: 0.15, radius: 0.4, threshold: 1 },
    grade: { contrast: 1.08, saturation: 0.95, warmth: 0.04, vignette: 0.22 },
    tiltShift: null,
    // #179: the map's mood graded in, and the finisher's background softened.
    mood: { intensity: 1 },
    dof: { aperture: 0.004, maxBlur: 0.012 },
  },

  // #179: sunlit motes hanging in the air around the rider.
  dust: { count: 260, size: 0.035, opacity: 0.55, radius: 9 },

  resolution: null,

  shadows: { mapSize: 4096, radius: 3, bias: -0.0003, normalBias: 0.03 },

  // Closer to human proportions on the same bones: a head about three
  // quarters the size (so the limbs read longer), smaller eyes, slimmer
  // limbs and a flatter chest, hands with fingers, lower-profile shoes with
  // real soles and laces, and
  // a natural face (lips, a longer jaw, a finer nose, matte eyes).
  rider: {
    head: 0.74,
    eyes: 0.62,
    face: "natural",
    limbs: 0.82,
    chest: { depth: 0.86, height: 1.06 },
    torso: "shaped",
    joints: "tapered",
    hands: "fingers",
    shoes: { length: 0.94, profile: 0.84 },
    feet: "natural",
    facets: 1,
  },
};
